import React, { useState, useEffect } from 'react';

import { Snackbar } from '@mui/material';

import Alert from 'domains/core/components/Alert';

type EmailExportSnackbarProps = {
    isDownloading: boolean;
    isSuccess: boolean;
    error: Error | null;
    emailName: string;
    onSuccessClosed?: () => void;
    onErrorClosed?: () => void;
};

const EmailExportSnackbar: React.FC<EmailExportSnackbarProps> = ({
    isDownloading,
    isSuccess,
    error,
    emailName,
    onSuccessClosed,
    onErrorClosed,
}) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<'success' | 'info' | 'error' | 'warning'>('info');
    const [autoHideDuration, setAutoHideDuration] = useState<number | null>(6000);

    useEffect(() => {
        if (isDownloading) {
            setMessage(`Downloading "${emailName}"...`);
            setSeverity('info');
            setAutoHideDuration(null);
            setOpen(true);
        } else if (isSuccess) {
            setMessage(`"${emailName}" successfully downloaded`);
            setSeverity('success');
            setAutoHideDuration(6000);
            setOpen(true);
        } else if (error) {
            setMessage(`Failed to download "${emailName}"`);
            setSeverity('error');
            setAutoHideDuration(6000);
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [isDownloading, isSuccess, error, emailName]);

    const handleClose = () => {
        setOpen(false);
        if (isSuccess && onSuccessClosed) {
            onSuccessClosed();
        } else if (error && onErrorClosed) {
            onErrorClosed();
        }
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleClose} severity={severity} variant="standard">
                {message}
            </Alert>
        </Snackbar>
    );
};

export default EmailExportSnackbar;
